import { useLocation } from 'react-router-dom';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// 💡 IMPORT CENTRALIZED SELECTORS AND AUTH ACTION
import { selectUser, selectIsAuthenticated, selectUserDashboardPath } from '../auth/authSelectors';
import { logoutLocal } from '../auth/authSlice'; 

// --- ICONS MOCKS & CONSTANTS ---
const FaFacebookF = () => <i className="fab fa-facebook-f" aria-hidden="true" />;
const FaInstagram = () => <i className="fab fa-instagram" aria-hidden="true" />;
const FaYoutube = () => <i className="fab fa-youtube" aria-hidden="true" />;
const FaAndroid = () => <i className="fab fa-android" aria-hidden="true" />;
const FaSignInAlt = () => <i className="fas fa-sign-in-alt" aria-hidden="true" />;
const FaUserPlus = () => <i className="fas fa-user-plus" aria-hidden="true" />;
const FaSignOutAlt = () => <i className="fas fa-sign-out-alt" aria-hidden="true" />; 
const FaBars = () => <i className="fas fa-bars" aria-hidden="true" />;
const FaLaptopCode = () => <i className="fas fa-laptop-code" aria-hidden="true" />;
const FaCheckCircle = () => <i className="fas fa-check-circle" aria-hidden="true" />;
const FaUsers = () => <i className="fas fa-users" aria-hidden="true" />;
const FaChartLine = () => <i className="fas fa-chart-line" aria-hidden="true" />;
const FaUserCircle = () => <i className="fas fa-user-circle" aria-hidden="true" />; 
const FaReceipt = () => <i className="fas fa-receipt" aria-hidden="true" />; 
const FaCaretDown = () => <i className="fas fa-caret-down" aria-hidden="true" />;
const FaTachometerAlt = () => <i className="fas fa-tachometer-alt" aria-hidden="true" />; 
const FaCog = () => <i className="fas fa-cog" aria-hidden="true" />; 



const getShortName = (name, max = 5) => {
  if (!name) return 'User';
  return name.length > max ? `${name.slice(0, max)}…` : name;
};

// SaaS-aligned colors and constants
export const PRIMARY_BLUE = '#0056d2'; 
export const SECONDARY_GREEN = '#28aa45';
export const APK_DOWNLOAD_URL = 'https://aadhavacademy.com/apk/AadhavAcademy.apk';

// Helper to safely get the global Bootstrap instance
const getBootstrap = () => window.bootstrap;

/**
 * Custom hook for managing the Bootstrap modal lifecycle.
 */
const useBootstrapModal = (modalRef) => {
    const bootstrap = getBootstrap();
    const modalInstanceRef = useRef(null); 

    useEffect(() => {
        if (!bootstrap || !bootstrap.Modal || !modalRef.current) {
            console.warn("Bootstrap Modal JS is not available or ref is null.");
            return;
        }

        try {
            let modal;
            
            // Check if getOrCreateInstance exists (Bootstrap 5.2.0+)
            if (typeof bootstrap.Modal.getOrCreateInstance === 'function') {
                modal = bootstrap.Modal.getOrCreateInstance(modalRef.current);
            } else {
                // Fallback for older Bootstrap 5 versions (< 5.2.0)
                modal = new bootstrap.Modal(modalRef.current);
            }
            
            modalInstanceRef.current = modal;
        } catch (error) {
            console.error("Failed to initialize Bootstrap Modal:", error);
        }
    }, [bootstrap, modalRef]); 

    const openModal = useCallback(() => {
        if (modalInstanceRef.current) {
            modalInstanceRef.current.show();
        } else {
            console.error("Bootstrap Modal instance not found. Cannot show.");
        }
    }, []);

    const closeModal = useCallback(() => {
        if (modalInstanceRef.current) {
            modalInstanceRef.current.hide();
        }
    }, []);

    return { openModal, closeModal };
};


// --- MAIN COMPONENT ---

export default function PublicLayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();     // ✅ MUST be inside component
  const isDemoPage = location.pathname.startsWith('/demo');

  // 🚀 REPLACED: const { isAuthenticated, user } = useSelector(state => state.auth || { isAuthenticated: false, user: null });
  // 🚀 NEW: Use centralized selectors to access state
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);
  const dashboardPath = useSelector(selectUserDashboardPath);
  
  const modalRef = useRef(null);
  const { openModal: openModalHook, closeModal } = useBootstrapModal(modalRef);
  
  // State for mobile navbar collapse (Managed entirely by React)
  const [isNavOpen, setIsNavOpen] = useState(false);
  // NEW state for the dropdown menu
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // State for the Demo Request Form
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    mobile: '',
    email: '',
  });

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // --- Utility Functions ---

  // 🗑️ REMOVED: This helper is now redundant, replaced by selectUserDashboardPath selector.
  // const getDashboardPath = () => {
  //   if (!user || !user.group_id) return '/dashboard';
  //   if (user.group_id === 1) return '/admin/dashboard';
  //   if (user.group_id === 2) return '/coach/dashboard';
  //   return '/student/dashboard';
  // };

  const handleLogout = () => { 
    dispatch(logoutLocal()); 
    navigate('/login'); // Redirect to login page
    setIsDropdownOpen(false); // Close dropdown on logout
    handleNavClick(); // Close main mobile nav
  };

  const openModal = () => {
    setMessage('');
    setFormData({ name: '', date: '', time: '', mobile: '', email: '' }); 
    openModalHook();
  };
  
  // Closes the navbar on click
  const handleNavClick = () => {
      setIsNavOpen(false);
  };
  
  // Closes the dropdown when the main nav is closed
  useEffect(() => {
    if (!isNavOpen) {
        setIsDropdownOpen(false);
    }
  }, [isNavOpen]);


  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/i.test(formData.mobile)) {
      setMessage('❌ Enter a valid 10-digit mobile number.');
      return;
    }
    if (!/\S+@\S+\.\S+/i.test(formData.email)) {
      setMessage('❌ Enter a valid professional email address.');
      return;
    }

    setLoading(true);
    setMessage('Submitting your request...');
    try {
      const response = await fetch('https://aadhavacademy.com/api/auth/book_demo.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage('✅ Demo requested successfully! We will contact you within 24 hours.');
        setTimeout(() => closeModal(), 3000); 
      } else {
        setMessage(`❌ ${result.message || 'Failed to submit request. Please try again.'}`);
      }
    } catch (error) {
      setMessage(`❌ Connection Error: The service is currently unavailable. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const RequestDemoButton = ({ onClick, children }) => (
    <button
        type="button"
        className="btn btn-primary btn-sm text-white fw-bold d-flex align-items-center justify-content-center px-3 py-2 w-100 w-lg-auto" 
        style={{
            background: PRIMARY_BLUE,
            border: 'none',
            borderRadius: '5px',
            boxShadow: `0 4px 12px ${PRIMARY_BLUE}40`,
            transition: 'all 0.3s ease',
            gap: '6px'
        }}
        onClick={onClick}
        aria-label="Request a Live Product Demonstration"
    >
        {children}
    </button>
);

const DownloadAppButton = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm text-white fw-bold d-flex align-items-center justify-content-center px-3 py-2 w-100 w-lg-auto" 
        style={{
            background: SECONDARY_GREEN,
            border: 'none',
            borderRadius: '5px',
            boxShadow: `0 4px 10px ${SECONDARY_GREEN}40`,
            transition: 'all 0.3s ease',
            gap: '6px'
        }}
        aria-label="Download the Aadhav Academy Mobile Application"
    >
        {children}
    </a>
);

  // --- RENDER ---
  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      
      {/* 1. HEADER / NAVIGATION */}
      {!isDemoPage && (
    <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm fixed-top border-bottom" style={{ minHeight: '56px', padding: '0.5rem 0' }}> 
  <div className="container"> 
    <Link to="/" className="navbar-brand d-flex align-items-center py-0" aria-label="Aadhav Academy Management Software Home">
<img 
  src="/images/academylogo.png" 
  alt="Aadhav Academy Logo" 
  style={{ 
    height: '48px',   // increase logo size
    maxHeight: '48px',
    width: 'auto',
    marginRight: '10px',
    objectFit: 'contain'
  }} 
/>
      <span className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}> 
        Aadhav <span style={{ color: PRIMARY_BLUE }}>Software</span>
      </span>
    </Link>
    
    {/* Mobile Toggle Button */}
    <button 
      className="navbar-toggler border-0 p-2" 
      type="button" 
      onClick={() => setIsNavOpen(!isNavOpen)} 
      aria-controls="navbarMenu" 
      aria-expanded={isNavOpen} 
      aria-label="Toggle navigation"
    >
      <FaBars style={{ color: PRIMARY_BLUE }} />
    </button>

    <div className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`} id="navbarMenu">
      {/* Primary Navigation Links - Aligned Left/Center on Desktop */}
      <ul className="navbar-nav mx-auto align-items-lg-center order-lg-1" onClick={handleNavClick}>
        {/* <li className="nav-item">
            <Link to="/competition" className="nav-link fw-normal px-3 text-dark">
                Competition
            </Link>
        </li> */}
        <li className="nav-item">
            <Link to="/tournament" className="nav-link fw-normal px-3 text-dark">
                Tournament
            </Link>
        </li>
         <li className="nav-item">
            <Link to="/productscomingsoon" className="nav-link fw-normal px-3 text-dark">
                Prodcuts
            </Link>
        </li>
        <li className="nav-item"><Link to="/" className="nav-link fw-normal px-3 text-dark">Platform</Link></li>
        <li className="nav-item"><Link to="/features" className="nav-link fw-normal px-3 text-dark">Features</Link></li>
        <li className="nav-item"><Link to="/pricing" className="nav-link fw-normal px-3 text-dark">Pricing</Link></li>
        <li className="nav-item"><Link to="/contact" className="nav-link fw-normal px-3 text-dark">Support</Link></li>
      </ul>

      {/* Action Buttons & Login - Aligned Right on Desktop */}
      <ul className="navbar-nav align-items-lg-center order-lg-2">
        
        {/* CONDITIONAL RENDERING FOR AUTHENTICATION - DROPDOWN */}
        {isAuthenticated ? (
            <li className={`nav-item dropdown me-lg-2 ${isDropdownOpen ? 'show' : ''}`} 
                onClick={(e) => {
                    e.stopPropagation(); 
                }}
            >
                {/* Profile Dropdown Toggle Button (Single Icon on Desktop, Name/Icon on Large screens) */}
                <button
                    className="nav-link fw-bold px-3 d-flex align-items-center justify-content-center justify-content-lg-start btn btn-link p-0 border-0"
                    style={{ color: PRIMARY_BLUE, textDecoration: 'none', gap: '8px' }} 
                    data-bs-toggle="dropdown" 
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-expanded={isDropdownOpen}
                    aria-label={`User Profile Menu for ${user?.name || 'User'}`}
                >
                    <FaUserCircle style={{ fontSize: '1.2rem' }} /> 
                <span
  className="d-lg-inline-block d-none"
  style={{
    maxWidth: '70px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }}
  title={user?.name}   // hover shows full name
>
  {getShortName(user?.name)}
</span>

                    <FaCaretDown className="ms-1" style={{ fontSize: '0.8rem' }} />
                </button>
                
                {/* Dropdown Menu */}
                <div 
                    className={`dropdown-menu dropdown-menu-end shadow border-0 rounded-3 ${isDropdownOpen ? 'show' : ''}`}
                    style={{ minWidth: '220px' }}
                    onClick={() => { setIsDropdownOpen(false); handleNavClick(); }} 
                >
                    
                    {/* User Info Header (Shows Name/Email) */}
                    <h6 className="dropdown-header small text-truncate fw-bold text-dark border-bottom pb-2 mb-2">
                        {user?.name || 'User Profile'}
                        <span className="fw-normal text-muted d-block" style={{ fontSize: '0.75rem' }}>
                            {user?.email || 'N/A'}
                        </span>
                    </h6>
                    
                

                    {/* Dashboard Link - FIXED SPACE HERE */}
                   <Link 
  to={dashboardPath}
  className="dropdown-item d-flex align-items-center"
>
  <span style={{ minWidth: '20px', marginRight: '0.5rem' }}>
    <FaTachometerAlt className="text-primary" />
  </span>
  Dashboard
</Link>


  {/* Profile Link */}
<Link 
    to="/profile" 
    className="dropdown-item d-flex align-items-center"
>
    <span style={{ minWidth: '20px', marginRight: '0.2rem', display: 'inline-block' }}>
        <FaCog className="text-info" />
    </span>
    Profile
</Link>

{/* Transactions Link */}
<Link 
    to="/transactions" 
    className="dropdown-item d-flex align-items-center"
>
    <span style={{ minWidth: '20px', marginRight: '0.2rem', display: 'inline-block' }}>
        <FaReceipt className="text-success" />
    </span>
    Transactions
</Link>

<div className="dropdown-divider"></div>

{/* Logout Button */}
<button 
    onClick={handleLogout}
    className="dropdown-item d-flex align-items-center text-danger fw-bold"
>
    <span style={{ minWidth: '20px', marginRight: '0.2rem', display: 'inline-block' }}>
        <FaSignOutAlt />
    </span>
    Logout
</button>

                </div>
            </li>
        ) : (
            // Login/Register Buttons
            <>
                {/* Login Button */}
                <li className="nav-item me-lg-2" onClick={handleNavClick}>
                    <Link 
                      to="/login"
                      className="nav-link fw-normal px-3 d-flex align-items-center justify-content-center justify-content-lg-start"
                      style={{ color: PRIMARY_BLUE, textDecoration: 'none', gap: '4px' }}
                    >
                      <FaSignInAlt /> Login
                    </Link>
                </li>

                {/* Register Button */}
                <li className="nav-item me-lg-2" onClick={handleNavClick}>
                    <Link
                      to="/register"
                      className="nav-link fw-normal px-3 d-flex align-items-center justify-content-center justify-content-lg-start"
                      style={{ color: PRIMARY_BLUE, textDecoration: 'none', gap: '4px' }}
                    >
                      <FaUserPlus /> Register
                    </Link>
                </li>
            </>
        )}
{/* 📞 Desktop-only Call Button (Visible ≥ 992px) */}
<li className="nav-item me-lg-2 mt-2 mt-lg-0 d-none d-lg-flex">
  <a
    href="tel:+919578686886"
    className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center"
    style={{
      whiteSpace: 'nowrap',
      gap: '6px',
      borderRadius: '20px',
      padding: '6px 12px'
    }}
    aria-label="Call Aadhav Academy Support"
  >
    📞 <span>+91 95786 86886</span>
  </a>
</li>

{/* 📱 Mobile-only phone number (TEXT ONLY, no click) */}
<li className="nav-item d-lg-none mt-2 text-center">
  <span
    className="fw-bold"
    style={{
      color: PRIMARY_BLUE,
      whiteSpace: 'nowrap'
    }}
  >
    📞 +91 95786 86886
  </span>
</li>



        {/* Request Live Demo (Always Visible) */}
        <li className="nav-item ms-lg-2 mt-2 mt-lg-0" onClick={handleNavClick}>
              <RequestDemoButton onClick={openModal}>
                <FaLaptopCode style={{ fontSize: '1.0rem' }} /> Demo
              </RequestDemoButton>
        </li>

        {/* Download App (Always Visible) */}
        {/* <li className="nav-item ms-lg-2 mt-2 mt-lg-0" onClick={handleNavClick}>
              <DownloadAppButton href={APK_DOWNLOAD_URL}>
                <FaAndroid style={{ fontSize: '1.0rem' }} /> App
              </DownloadAppButton>
        </li> */}
      </ul>
    </div>
  </div>
</nav>
)}

      {/* Page Content Outlet */}
     <main className="flex-grow-1" style={{ paddingTop: '80px' }}> 
  <Outlet context={{ openDemoModal: openModal }} /> 
</main>

      {/* 2. FOOTER */}
      <footer className="bg-dark text-light mt-auto">
        <div className="container py-5">
          <div className="row gy-4">
            <div className="col-md-4 text-center text-md-start">
              <h6 className="fw-bold mb-3 text-uppercase" style={{ color: PRIMARY_BLUE }}>Aadhav **Software**</h6>
              <p className="small text-secondary mb-0">
                The all-in-one **SaaS solution** designed specifically for chess and sports academies. Streamline enrollment, fee collection, and progress reporting with our powerful management platform.
              </p>
            </div>
            <div className="col-md-4 text-center">
              <h6 className="fw-bold mb-3 text-uppercase">Product Links</h6>
              <ul className="list-unstyled small mb-0">
                <li><Link to="/features" className="text-secondary text-decoration-none py-1 d-block">Software Features</Link></li>
                <li><Link to="/pricing" className="text-secondary text-decoration-none py-1 d-block">Pricing Plans</Link></li>
                <li><Link to="/contact" className="text-secondary text-decoration-none py-1 d-block">Contact Sales</Link></li>
                <li><a href={APK_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="text-secondary text-decoration-none py-1 d-block"><FaAndroid className="me-1" /> Get Mobile App</a></li>
              </ul>
            </div>
            <div className="col-md-4 text-center">
              <h6 className="fw-bold mb-3 text-uppercase">Connect</h6>
              <div className="d-flex justify-content-center justify-content-md-end align-items-center gap-3">
                {[
                  { icon: <FaFacebookF />, color: '#1877f2', link: 'https://www.facebook.com' },
                  { icon: <FaInstagram />, color: '#E1306C', link: 'https://www.instagram.com' },
                  { icon: <FaYoutube />, color: '#FF0000', link: 'https://www.youtube.com' },
                ].map((item, i) => (
                  <a key={i} href={item.link} target="_blank" rel="noopener noreferrer"
                     aria-label={`Follow us on ${item.link.split('.')[1]}`}
                     className="d-flex align-items-center justify-content-center rounded-circle border border-secondary"
                     style={{ width: '38px', height: '38px', color: '#ffffff', transition: 'all 0.3s ease', fontSize: '0.9rem' }}
                     onMouseEnter={(e) => { e.currentTarget.style.color = item.color; e.currentTarget.style.borderColor = item.color; }}
                     onMouseLeave={(e) => { e.currentTarget.style.color = '#ffffff'; e.currentTarget.style.borderColor = '#6c757d'; }}
                  >
                    {item.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <hr className="border-secondary my-4" />
          <div className="text-center small text-secondary">© {new Date().getFullYear()} **Aadhav Academy Management Software**. All Rights Reserved.</div>
        </div>
      </footer>

      {/* 3. LIVE DEMO MODAL */}
      <div
        className="modal fade"
        ref={modalRef}
        tabIndex="-1"
        aria-hidden="true"
        aria-labelledby="demoModalLabel"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl modal-fullscreen-sm-down">
          <div className="modal-content border-0 rounded-4 shadow-lg">
            
            <div className="row g-0">
                <div className="col-lg-7 d-flex flex-column" style={{ minHeight: '100%' }}>
                    
                    <div className="p-4 p-md-5 flex-grow-1 overflow-auto">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 id="demoModalLabel" className="fw-bold text-dark d-flex align-items-center mb-0">
                              <FaLaptopCode className="me-2" style={{ color: PRIMARY_BLUE, fontSize: '1.5rem' }} /> Schedule Your Live Demo
                            </h4>
                            <button
                              type="button"
                              className="btn-close"
                              onClick={closeModal}
                              aria-label="Close"
                            ></button>
                        </div>

                        <p className="text-secondary small mb-4">Book a **personalized** 30-minute session with our product specialist. See how Aadhav Software can transform your academy's efficiency.</p>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label htmlFor="name" className="form-label fw-semibold">Your Full Name</label>
                                <input
                                    id="name"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="form-control rounded-3 form-control-lg" 
                                    placeholder="e.g., Jane Doe, Academy Manager"
                                    required
                                />
                            </div>
                            
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="date" className="form-label fw-semibold">Preferred Date</label>
                                    <input
                                        id="date"
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        className="form-control rounded-3 form-control-lg"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label htmlFor="time" className="form-label fw-semibold">Preferred Time</label>
                                    <input
                                        id="time"
                                        type="time"
                                        name="time"
                                        value={formData.time}
                                        onChange={handleChange}
                                        className="form-control rounded-3 form-control-lg"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label htmlFor="mobile" className="form-label fw-semibold">Mobile Number</label>
                                <input
                                    id="mobile"
                                    type="tel"
                                    name="mobile"
                                    pattern="\d{10}"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    className="form-control rounded-3 form-control-lg"
                                    placeholder="10-digit mobile number for contact"
                                    required
                                />
                            </div>
                            
                            <div className="mb-4">
                                <label htmlFor="email" className="form-label fw-semibold">Professional Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-control rounded-3 form-control-lg"
                                    placeholder="e.g., manager@academy.com"
                                    required
                                />
                            </div>

                            {message && (
                                <div
                                    className={`alert py-2 mt-3 text-center ${
                                        message.startsWith('✅') ? 'alert-success' : 'alert-danger'
                                    }`}
                                >
                                    {message}
                                </div>
                            )}
                            
                            <div className="d-flex border-top pt-4 mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary rounded-3 px-4 flex-fill me-2"
                                    onClick={closeModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn text-white fw-bold rounded-3 px-4 flex-fill"
                                    disabled={loading}
                                    style={{
                                        backgroundColor: PRIMARY_BLUE,
                                        border: 'none',
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Request'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="col-lg-5 d-none d-lg-block rounded-end-4" style={{ backgroundColor: '#f8f9fa', borderLeft: '1px solid #eee' }}>
                    <div className="p-5">
                        <h5 className="fw-bold mb-4" style={{ color: PRIMARY_BLUE }}>What to Expect in Your Demo:</h5>
                        <ul className="list-unstyled small">
                            {[
                                { icon: <FaCheckCircle className="text-success me-3" />, text: '**Personalized Walk-through:** Tailored to your specific academy type (Chess, Sports, etc.).' },
                                { icon: <FaUsers className="text-info me-3" />, text: '**Client Management Deep Dive:** See student enrollment, communication, and attendance in action.' },
                                { icon: <FaChartLine className="text-warning me-3" />, text: '**Financial Operations:** Live demonstration of fee collection, billing, and analytics dashboards.' },
                                { icon: <FaLaptopCode className="text-primary me-3" />, text: '**Direct Q&A:** A product specialist will address all your technical and business queries.' },
                            ].map((item, index) => (
                                <li key={index} className="d-flex align-items-start mb-3">
                                    {item.icon}
                                    <span className="text-dark">{item.text}</span>
                                </li>
                            ))}
                        </ul>
                        <hr className="my-4" />
                        <p className="small text-muted mb-0">
                            **Commitment to Privacy:** Your data is safe with us. We use the information provided only to schedule your customized demo session.
                        </p>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}